import ImgRow from '../components/StrapiPage/ImgRow'
import TextRow from '../components/StrapiPage/TextRow'
import ColumnsRow from '../components/StrapiPage/ColumnsRow'
import TestimonialRow from '../components/StrapiPage/TestimonialRow'
import CustomerTabsRow from '../components/StrapiPage/CustomerTabsRow'
import IntegrationsRow from '../components/StrapiPage/IntegrationsRow'
import {get} from 'lodash'
import React from 'react'
import InviteRow from '../components/StrapiPage/InviteRow'
import CustomersRow from '../components/StrapiPage/CustomersRow'
import FormRow from '../components/StrapiPage/FormRow'
import FaqsRow from '../components/StrapiPage/fagsRow'
import ContentTabsRow from '../components/StrapiPage/ContentTabsRow'
import TextWithHighlights from '../components/StrapiPage/TextWithHighlights'

const componentsNames = {
    imgRow: 'page.img-row',
    textRow: 'page.text-row',
    columnsRow: 'page.columns-row',
    testimonialRow: 'page.testimonial-row',
    customerTabsRow: 'page.customer-tabs-row',
    integrationsRow: 'page.integrations-row',
    inviteRow: 'page.invite-row',
    customersRow: 'page.customers-row',
    formRow: 'page.form-row',
    fagsRow: 'page.faqs-row',
    contentTabsRow: 'page.content-tabs-row',
    textWithHighlights: 'page.text-with-highlights',
}

const componentMap = {
    [componentsNames.imgRow]: ImgRow,
    [componentsNames.textRow]: TextRow,
    [componentsNames.columnsRow]: ColumnsRow,
    [componentsNames.testimonialRow]: TestimonialRow,
    [componentsNames.customerTabsRow]: CustomerTabsRow,
    [componentsNames.integrationsRow]: IntegrationsRow,
    [componentsNames.inviteRow]: InviteRow,
    [componentsNames.customersRow]: CustomersRow,
    [componentsNames.formRow]: FormRow,
    [componentsNames.fagsRow]: FaqsRow,
    [componentsNames.contentTabsRow]: ContentTabsRow,
    [componentsNames.textWithHighlights]: TextWithHighlights,
}

export const pageComponentRenderer = () => {
    return (component) => {
        const Element = matchComponent(component._xcomponent)
        const props = extractProps(component)
        return Element ? <Element key={props.id} {...props} /> : null
    }
}

const matchComponent = (componentName) => {
    return componentMap[componentName]
}

const extractProps = (component) => {
    switch (component._xcomponent) {
        case componentsNames.imgRow:
            return {
                id: generateId(component),
                content: get(component, 'content', ''),
                imageData: get(component, 'image.image.childImageSharp.gatsbyImageData', null),
                imageAlt: get(component, 'image.alt', ''),
                imagePosition: component.image_position,
                imageSize: component.image_size,
                backgroundColor: getBackgroundColor(component),
            }
        case componentsNames.textRow:
            return {
                id: generateId(component),
                content: get(component, 'content', ''),
                contentAlignment: getContentAlignment(component),
                backgroundColor: getBackgroundColor(component),
            }
        case componentsNames.columnsRow:
            return {
                id: generateId(component),
                title: get(component, 'title', ''),
                columns: component.columns.map((column) => ({
                    id: column.id,
                    content: get(column, 'content', ''),
                    imageData: get(column, 'image.image.childImageSharp.gatsbyImageData', null),
                    imageAlt: get(column, 'image.alt', ''),
                    buttonLink: get(column, 'button_link', undefined),
                })),
                columnsStyle: component.column_style,
                backgroundColor: getBackgroundColor(component),
            }
        case componentsNames.testimonialRow:
            return {
                id: generateId(component),
                content: get(component, 'content', ''),
                author: get(component, 'author', ''),
                imageData: get(component, 'image.image.childImageSharp.gatsbyImageData', null),
                imageAlt: get(component, 'image.alt', ''),
            }
        case componentsNames.customerTabsRow:
            return {
                id: generateId(component),
                title: component.title,
                tabs: component.tabs.map((tab) => ({
                    id: tab.id,
                    content: get(tab, 'content', ''),
                    imageData: get(tab, 'image.image.childImageSharp.gatsbyImageData', null),
                    imageAlt: get(tab, 'image.alt', ''),
                    customerId: get(tab, 'customer.id', null),
                })),
            }
        case componentsNames.integrationsRow:
            return {
                id: generateId(component),
                integrationIds: component.integrations,
                backgroundColor: getBackgroundColor(component),
            }
        case componentsNames.inviteRow:
            return {
                id: generateId(component),
                content: get(component, 'content', ''),
                imageData: get(component, 'image.image.childImageSharp.gatsbyImageData', null),
                imageAlt: get(component, 'image.alt', ''),
                backgroundColor: getBackgroundColor(component),
                buttons: getButtons(component),
            }
        case componentsNames.customersRow:
            return {
                id: generateId(component),
                title: component.title,
                customerIds: component.customers,
                backgroundColor: getBackgroundColor(component),
                grayscale: component.grayscale,
            }
        case componentsNames.formRow:
            return {
                id: generateId(component),
                content: get(component, 'content', ''),
                formId: get(component, 'form.id', ''),
                formName: get(component, 'form.name', ''),
            }
        case componentsNames.fagsRow:
            return {
                id: generateId(component),
                title: get(component, 'title', ''),
                faqs: get(component, 'faqs', []),
            }
        case componentsNames.contentTabsRow:
            return {
                id: generateId(component),
                title: get(component, 'title', ''),
                tabs: component.tabs.map((tab) => ({
                    id: tab.id,
                    title: get(tab, 'title', ''),
                    imageData: get(tab, 'image.image.childImageSharp.gatsbyImageData', null),
                    imageAlt: get(tab, 'image.alt', ''),
                })),
            }
        case componentsNames.textWithHighlights:
            return {
                id: generateId(component),
                title: get(component, 'title', ''),
                description: get(component, 'description', ''),
                highlights: component.highlight.map((highlight) => ({
                    id: highlight.id,
                    title: get(highlight, 'title', ''),
                    text: get(highlight, 'text', ''),
                    link: get(highlight, 'link', ''),
                })),
                backgroundColor: getBackgroundColor(component),
            }
        default:
            return {}
    }
}

const generateId = (component) => `${component._xcomponent}-${component.id}`

const getBackgroundColor = (component) => get(component, 'background.background_color', null)

const getContentAlignment = (component) => get(component, 'content_alignment', 'center')

const getButtons = (component) => get(component, 'buttons', null)
